import { useCallback, useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  PlusIcon,
  XIcon,
  CalendarIcon,
  CalculatorIcon,
  MailIcon,
  ToggleLeftIcon,
  PhoneIcon,
  TypeIcon,
  Trash2Icon,
  TagIcon,
  PlayIcon,
} from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { JsonValue } from 'type-fest';
import * as yup from 'yup';

import { insightsEndpoints } from '@/api/endpoints';
import { AuthoringValidationBanner } from '@/components/atoms/AuthoringValidationBanner/AuthoringValidationBanner';
import { NumbersIcon } from '@/components/atoms/Icons/Numbers';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
  SidebarWrapperForm,
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { useInsights } from '@/components/pages/Insights/useInsights';
import { Button } from '@/components/ui/button';
import { AdvancedSection } from '@/components/ui/custom/AdvanvedSection';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import useBrains from '@/hooks/useBrains';
import { useDraftValidation } from '@/hooks/useDraftValidation';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { setCurrentInsightId, setIsDuplicatingInsight } from '@/redux/insights/actions';
import { selectCurrentInsightId, selectIsDuplicatingInsight } from '@/redux/insights/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import type { ValidateInsightBody } from '@/shared/types/authoring-validation';
import { BrainType } from '@/shared/types/brain';
import { Insight, INSIGHT_TYPES } from '@/shared/types/insights';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { isKeyEnter, hasDirtyFields, useHasDirtyFields } from '@/util/util';

import { InsightTestSection } from './InsightTestSection';
import { buildInsightValidateBody, createInsightSchema, convertToValidInsight } from './validator';

interface AddInsightProps {
  toggleSidebar: (sidebar: 'insights' | null) => void;
  insightToEdit?: Insight;
}

type FormValues = yup.InferType<ReturnType<typeof createInsightSchema>>;

/**
 * Gets the appropriate icon component for a given insight type
 * @param type - The insight type
 */
const getInsightTypeIcon = (type: string) => {
  switch (type) {
    case INSIGHT_TYPES.DATETIME:
      return CalendarIcon;
    case INSIGHT_TYPES.INTEGER:
      return NumbersIcon;
    case INSIGHT_TYPES.NUMBER:
      return CalculatorIcon;
    case INSIGHT_TYPES.EMAIL:
      return MailIcon;
    case INSIGHT_TYPES.BOOLEAN:
      return ToggleLeftIcon;
    case INSIGHT_TYPES.PHONE_NUMBER:
      return PhoneIcon;
    case INSIGHT_TYPES.STRING:
      return TypeIcon;
    case INSIGHT_TYPES.CATEGORICAL:
      return TagIcon;
    default:
      return TypeIcon;
  }
};

const getNamePlaceholderKey = (brainType: BrainType | undefined) => {
  switch (brainType) {
    case BrainType.SUPPORT:
      return 'ai_agents.name_placeholder_support';
    case BrainType.ACTIVATION:
      return 'ai_agents.name_placeholder_activation';
    case BrainType.EARLY_ENGAGEMENT:
      return 'ai_agents.name_placeholder_engagement';
    case BrainType.UPSELL:
      return 'ai_agents.name_placeholder_upsell';
    case BrainType.DEBT_COLLECTION:
    default:
      return 'ai_agents.name_placeholder';
  }
};

const getGuidelinePlaceholderKey = (brainType: BrainType | undefined) => {
  switch (brainType) {
    case BrainType.SUPPORT:
      return 'ai_agents.guideline_placeholder_support';
    case BrainType.ACTIVATION:
      return 'ai_agents.guideline_placeholder_activation';
    case BrainType.EARLY_ENGAGEMENT:
      return 'ai_agents.guideline_placeholder_engagement';
    case BrainType.UPSELL:
      return 'ai_agents.guideline_placeholder_upsell';
    case BrainType.DEBT_COLLECTION:
    default:
      return 'ai_agents.guideline_placeholder';
  }
};

export const AddInsight = (props: AddInsightProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { brain, insightsEnabled } = useBrains(brainId);
  const namePlaceholderKey = getNamePlaceholderKey(brain?.brain_type);
  const guidelinePlaceholderKey = getGuidelinePlaceholderKey(brain?.brain_type);
  const insightId = useSelector(selectCurrentInsightId);
  const isDuplicatingInsight = useSelector(selectIsDuplicatingInsight);
  const {
    createInsight,
    updateInsight,
    selectedInsight,
    selectedInsightStatus,
    createStatus,
    updateStatus,
    resetUpdateMutation,
    testInsight,
    testStatus,
  } = useInsights(brainId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const readonly = !insightsEnabled || !canWrite;

  // Test functionality state
  const [testSessionId, setTestSessionId] = useState<string>('');
  const [testResult, setTestResult] = useState<JsonValue | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSectionOpen, setTestSectionOpen] = useState<string>('');
  const [hasAttemptedTest, setHasAttemptedTest] = useState<boolean>(false);

  // Unsaved changes modal state
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Track if form has been populated via duplication
  const [hasBeenPopulatedViaDuplication, setHasBeenPopulatedViaDuplication] = useState(false);

  // Lets the create-mode reset effect skip its reset while closing after an update, or while
  // leaving duplicate mode on submit.
  const skipCreateResetRef = useRef(false);

  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(
      createInsightSchema(brainId, selectedInsight?.name)
    ),
    defaultValues: { name: '', description: '', type: INSIGHT_TYPES.STRING, categories: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { fields, remove, prepend } = useFieldArray({ control: form.control, name: 'categories' });

  // Focus on the next frame so it lands after the panel has mounted.
  const focusFrameRef = useRef<number | undefined>(undefined);
  const focusField = useCallback(
    (fieldName: 'name' | 'description') => {
      if (focusFrameRef.current !== undefined) {
        cancelAnimationFrame(focusFrameRef.current);
      }
      focusFrameRef.current = requestAnimationFrame(() => form.setFocus(fieldName));
    },
    [form]
  );

  // Cancel a pending focus frame if the panel unmounts before it fires.
  useEffect(() => {
    return () => {
      if (focusFrameRef.current !== undefined) {
        cancelAnimationFrame(focusFrameRef.current);
      }
    };
  }, []);

  const selectedType = useWatch({ control: form.control, name: 'type' });
  const isCategorical = selectedType === INSIGHT_TYPES.CATEGORICAL;

  // Disable Create while a new insight has no name and no guideline.
  const nameValue = useWatch({ control: form.control, name: 'name' });
  const descriptionValue = useWatch({ control: form.control, name: 'description' });
  const isNewInsightEmpty =
    !insightId && !isDuplicatingInsight && !nameValue?.trim() && !descriptionValue?.trim();

  // Extract form state properties for button management
  const { formState, getFieldState } = form;
  const { isSubmitSuccessful } = formState;
  const isDirty = useHasDirtyFields(form.control);
  // Read through `formState`, not `errors`: RHF mutates `errors` in place, and `form` is a stable
  // object, so the React Compiler would cache the first answer of `form.formState.errors`.
  // `formState` is a new object on every form state change. `root` is where RHF puts a
  // field-array-level error.
  const categoriesError = getFieldState('categories', formState).error?.root?.message;

  const {
    recommendation,
    scheduleValidate,
    reset: resetValidation,
  } = useDraftValidation<ValidateInsightBody>({
    endpoint: brainId ? insightsEndpoints.validate(brainId) : '',
  });

  // Live, debounced validation: every field edit schedules a validate call; the hook
  // coalesces rapid edits into a single request after the idle window. Advisory only —
  // the recommendation banner never blocks the submit button.
  const validateDraft = () => {
    const body = brainId ? buildInsightValidateBody(form.getValues()) : null;
    if (body) {
      scheduleValidate(body);
    } else {
      resetValidation();
    }
  };

  // Switching between insights (or entering/leaving duplicate mode) populates the form
  // from a different source. Clear any banner from the previous context so the user
  // doesn't see stale advice.
  useEffect(() => {
    resetValidation();
  }, [insightId, isDuplicatingInsight, resetValidation]);

  useTrackFormState({ isDirty: isDirty || hasBeenPopulatedViaDuplication, formId: 'add-insight' });

  /**
   * Resets all test-related state data
   */
  const resetTestData = useCallback(() => {
    setTestSessionId('');
    setTestResult(null);
    setTestError(null);
    setHasAttemptedTest(false);
  }, []);

  // Populate form when editing or duplicating an insight
  useEffect(() => {
    if (selectedInsight && insightId && selectedInsightStatus === 'success') {
      const nameToUse = isDuplicatingInsight
        ? `${selectedInsight.name}_copy`
        : selectedInsight.name;

      const formData = {
        name: nameToUse,
        description: selectedInsight.description ?? '',
        type: selectedInsight.type,
        categories: selectedInsight.categories?.map((cat) => ({ value: cat })) || [],
      };

      if (isDuplicatingInsight) {
        // When duplicating, reset form but keep default values unchanged to preserve dirty state
        form.reset(formData, { keepDefaultValues: true });
        setHasBeenPopulatedViaDuplication(true);
        focusField('name');
        return;
      }

      // When editing, defer the reset a frame so the type Select reflects the value when
      // reopening from cache; an immediate reset leaves the Select on its placeholder.
      const frame = requestAnimationFrame(() => {
        form.reset(formData, { keepDefaultValues: false });
        // Name is disabled while editing, so focus the guideline instead.
        focusField('description');
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [selectedInsight, insightId, selectedInsightStatus, form, isDuplicatingInsight, focusField]);

  // Handle transition to create mode - reset form when switching from edit/duplicate to create
  useEffect(() => {
    if (insightId === null && !isDuplicatingInsight) {
      // Closing after an update or submitting a duplicate, not opening create mode: skip the reset.
      if (skipCreateResetRef.current) {
        skipCreateResetRef.current = false;
        return;
      }
      form.reset({ name: '', description: '', type: INSIGHT_TYPES.STRING, categories: [] });
      resetTestData();
      focusField('name');
      // Clear the duplication state
      setHasBeenPopulatedViaDuplication(false);
    }
  }, [insightId, isDuplicatingInsight, form, resetTestData, focusField]);

  // Handle type changes - cleanup and initialize categories
  useEffect(() => {
    if (isCategorical && fields.length === 0) {
      prepend({ value: '' }); // Add first empty category
    } else if (!isCategorical && fields.length > 0) {
      // Clear categories when switching away from categorical
      form.setValue('categories', []);
    }
  }, [isCategorical, fields.length, form, prepend]);

  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form) || hasBeenPopulatedViaDuplication;

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      // Clear insight ID and duplication flag when closing sidebar
      if (insightId) {
        dispatch(setCurrentInsightId(null));
      }
      if (isDuplicatingInsight) {
        dispatch(setIsDuplicatingInsight(false));
      }
      props.toggleSidebar(null);
    }
  };

  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  const onSubmit = (data: FormValues) => {
    const categories = data.categories
      .map((cat) => cat.value)
      .filter((category) => category.trim() !== '');
    const insightData = {
      name: data.name,
      description: data.description,
      type: data.type,
      categories,
    };

    if (insightId && selectedInsight && !isDuplicatingInsight) {
      updateInsight({ insightId, data: insightData });
      return;
    }

    // Leave duplicate mode now: a `mutate` callback is skipped once the panel unmounts. Skip the
    // create-mode reset that leaving it would run, so a rejected duplicate keeps its input.
    if (isDuplicatingInsight) {
      skipCreateResetRef.current = true;
      dispatch(setIsDuplicatingInsight(false));
      dispatch(setCurrentInsightId(null));
      setHasBeenPopulatedViaDuplication(false);
    }

    // Reset only once the insight exists, so a rejected create keeps what the user typed.
    createInsight(insightData, {
      onSuccess: () => {
        form.reset();
        resetValidation();
      },
    });
  };

  // Close the sidebar after a successful update.
  useEffect(() => {
    if (updateStatus === 'success' && insightId) {
      skipCreateResetRef.current = true;
      resetUpdateMutation();
      props.toggleSidebar(null);
      dispatch(setCurrentInsightId(null));
    }
  }, [updateStatus, insightId, dispatch, resetUpdateMutation, props]);

  /**
   * Checks if there are any empty categories that should prevent adding new ones
   */
  const categoryValues = useWatch({ control: form.control, name: 'categories' });
  const hasEmptyCategories = fields.some((_, index) => {
    const value = categoryValues?.[index]?.value;
    return !value || value.trim() === '';
  });

  /**
   * Checks if the maximum number of categories (10) has been reached
   */
  const isMaxCategoriesReached = fields.length >= 10;

  /**
   * Re-checks the whole list after a submit. A row edit only revalidates that row, so the list-level
   * errors (no category, duplicates) would otherwise stay until the next submit.
   */
  const revalidateCategories = () => {
    if (formState.isSubmitted) {
      form.trigger('categories');
    }
  };

  /**
   * Removes a category at the specified index
   */
  const removeCategory = (index: number) => {
    remove(index);
    revalidateCategories();
    validateDraft();
  };

  /**
   * Adds a new category at the end of the list
   */
  const addCategory = useCallback(() => {
    prepend({ value: '' }, { shouldFocus: true, focusName: 'categories.0' });
  }, [prepend]);

  // Signal to the user that the validator has advice — "anyway" tells them they're
  // saving over a warning. Non-blocking either way.
  const getSubmitButtonText = useCallback(() => {
    const isUpdate = Boolean(insightId) && !isDuplicatingInsight;
    if (recommendation) {
      return isUpdate ? t('common.update_anyway') : t('common.create_anyway');
    }
    return isUpdate ? t('common.update') : t('common.create');
  }, [insightId, isDuplicatingInsight, recommendation, t]);

  /**
   * Handles changes to the test section accordion state
   * Resets test data when section is closed
   */
  const handleTestSectionChange = useCallback(
    (value: string) => {
      setTestSectionOpen(value);

      // If section is being closed (value becomes empty), reset test data
      if (value === '') {
        resetTestData();
      }
    },
    [resetTestData]
  );

  /**
   * Handles testing the insight with the provided session ID
   */
  const handleTest = () => {
    if (!testSessionId.trim()) {
      setTestError(t('insights.test_with_conversation_error'));
      return;
    }

    // Get current form values
    const formValues = form.getValues();

    // Create insight object from form data
    const insight: Partial<Insight> = {
      name: formValues.name,
      description: formValues.description,
      type: formValues.type,
      categories: formValues.categories
        .map((cat) => cat.value)
        .filter((category) => category.trim() !== ''),
    };

    const insightData = { session_id: testSessionId, insight };

    // Validate form is complete enough for testing
    if (!insightData.insight.name?.trim() || !insightData.insight.description?.trim()) {
      // Please fill in name and description before testing
      setTestError(t('insights.test_with_conversation_fill_fields'));
      return;
    }

    // Clear previous results
    setTestError(null);
    setTestResult(null);
    setHasAttemptedTest(true);

    // Call the test API with callbacks
    testInsight({
      data: insightData,
      onSuccess: (response) => {
        setTestResult(response.value);
        setTestError(null);
      },
      onError: (error) => {
        setTestError(error.message);
        setTestResult(null);
      },
    });
  };
  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {insightId && !isDuplicatingInsight
            ? t('ai_agents.edit_insight')
            : t('ai_agents.create_insight')}
        </h2>

        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>
      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1">
            {/* Name Field */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(namePlaceholderKey)}
                      onChange={(e) => {
                        const val = convertToValidInsight(e.target.value);
                        field.onChange(val);
                        form.trigger('name');
                        validateDraft();
                      }}
                      disabled={readonly || (insightId && !isDuplicatingInsight)}
                    />
                  </FormControl>
                  <p className="text-sm text-foreground-muted">{t('ai_agents.name_description')}</p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description Field */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ai_agents.guideline')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t(guidelinePlaceholderKey)}
                      onChange={(e) => {
                        field.onChange(e);
                        validateDraft();
                      }}
                      className="min-h-24"
                      disabled={readonly}
                    />
                  </FormControl>
                  <p className="text-sm text-foreground-muted">
                    {t('ai_agents.guideline_description')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Type Field */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ai_agents.insight_type')}</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (!value) {
                        return;
                      }
                      field.onChange(value);
                      validateDraft();
                    }}
                    value={field.value}
                    disabled={readonly}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ai_agents.insight_type')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(INSIGHT_TYPES).map(([key, value]) => {
                        const IconComponent = getInsightTypeIcon(value);
                        return (
                          <SelectItem key={key} value={value}>
                            <div className="flex items-center gap-2">
                              <IconComponent />
                              {t(`insights.${value.toLowerCase()}`)}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-foreground-muted">
                    {t('ai_agents.insight_type_description')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Categories Field - Only shown when type is CATEGORICAL */}
            {isCategorical && (
              <>
                <Label className="mb-2">{t('ai_agents.categories')}</Label>
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`categories.${index}.value`}
                        render={({ field: categoryField }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input
                                {...categoryField}
                                placeholder={t('insights.category_placeholder')}
                                onChange={(e) => {
                                  const val = convertToValidInsight(e.target.value);
                                  categoryField.onChange(val);
                                  revalidateCategories();
                                  validateDraft();
                                }}
                                onKeyDown={(e) => {
                                  if (isKeyEnter(e)) {
                                    e.preventDefault();

                                    const currentValue = form.getValues(
                                      `categories.${index}.value`
                                    );
                                    if (
                                      currentValue &&
                                      currentValue.trim() !== '' &&
                                      !isMaxCategoriesReached
                                    ) {
                                      addCategory();
                                    }
                                  }
                                }}
                                disabled={readonly}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {index === 0 && !isMaxCategoriesReached && (
                        <Button
                          type="button"
                          variant="secondary-neutral"
                          size="icon-md"
                          onClick={addCategory}
                          disabled={readonly || hasEmptyCategories}
                        >
                          <PlusIcon size={16} />
                        </Button>
                      )}
                      {((index === 0 && isMaxCategoriesReached) || index > 0) && (
                        <Button
                          aria-label={t('common.remove')}
                          className="hover:text-destructive hover:[&_svg]:text-destructive"
                          type="button"
                          variant="ghost-neutral"
                          size="icon-md"
                          onClick={() => removeCategory(index)}
                          disabled={readonly}
                        >
                          <Trash2Icon size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {categoriesError && <p className="text-sm text-warning">{categoriesError}</p>}
              </>
            )}

            {/* Test with conversation */}
            <AdvancedSection
              title={t('insights.test_with_conversation')}
              titleIcon={<PlayIcon size={16} />}
              value={testSectionOpen}
              onValueChange={handleTestSectionChange}
            >
              <InsightTestSection
                testSessionId={testSessionId}
                setTestSessionId={setTestSessionId}
                testResult={testResult}
                testError={testError}
                testStatus={testStatus}
                hasAttemptedTest={hasAttemptedTest}
                onTest={handleTest}
                readonly={readonly}
              />
            </AdvancedSection>

            <AuthoringValidationBanner recommendation={recommendation} />
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button
              type="submit"
              isLoading={createStatus === 'pending' || updateStatus === 'pending'}
              disabled={readonly || isNewInsightEmpty || (isSubmitSuccessful && !isDirty)}
            >
              {getSubmitButtonText()}
            </Button>
            <Button
              type="button"
              variant="ghost-neutral"
              onClick={handleClose}
              disabled={isSubmitSuccessful && !isDirty}
            >
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            // Clear insight ID and duplication flag when discarding changes
            if (insightId) {
              dispatch(setCurrentInsightId(null));
            }
            if (isDuplicatingInsight) {
              dispatch(setIsDuplicatingInsight(false));
            }
            // Clear the local duplication state
            setHasBeenPopulatedViaDuplication(false);
            props.toggleSidebar(null);
          }}
          onGoBack={() => {
            setIsUnsavedChangesModalOpen(false);
          }}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
